import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../services/api';
import './TopProgressBar.css';

function TopProgressBar({ job, status, onCancel, onDismiss, userId, apiBaseUrl }) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [visible, setVisible] = useState(false);
  const elapsedTimerRef = useRef(null);
  const autoDismissRef = useRef(null);

  const isTerminal = status?.status === 'success' || status?.status === 'failed' || status?.status === 'cancelled';
  const isProcessing = status?.status === 'processing';

  // Show/hide logic
  useEffect(() => {
    if (isProcessing || isTerminal) {
      setVisible(true);
    }
  }, [isProcessing, isTerminal]);

  // Auto-dismiss after terminal status
  useEffect(() => {
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }

    if (isTerminal && visible) {
      autoDismissRef.current = setTimeout(() => {
        setVisible(false);
        if (onDismiss) onDismiss();
      }, 8000);
    }

    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    };
  }, [isTerminal, visible, onDismiss]);

  // Elapsed timer
  useEffect(() => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    if (isProcessing && status?.timing?.started_at) {
      const updateElapsed = () => {
        let ts = status.timing.started_at;
        if (!ts.endsWith('Z') && !ts.match(/[+-]\d{2}:\d{2}$/)) ts += 'Z';
        const elapsed = Math.max(0, Math.floor((new Date() - new Date(ts)) / 1000));
        setElapsedTime(elapsed);
      };
      updateElapsed();
      elapsedTimerRef.current = setInterval(updateElapsed, 1000);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, [isProcessing, status?.timing?.started_at]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined || seconds < 0 || isNaN(seconds)) return 'Calculating...';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const handleCancel = async () => {
    if (!job?.id) return;
    if (!window.confirm('Are you sure you want to cancel this job?')) return;

    try {
      await api.cancelJob(job.id);
      if (onCancel) onCancel({ job_id: job.id, status: 'cancelled' });
    } catch (error) {
      console.error('Error canceling job:', error);
      alert('Failed to cancel job. Please try again.');
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!status) return null;

  const percent = status.progress?.percent || 0;

  const statusConfig = {
    success: { icon: '\u2705', message: `Job completed successfully \u2014 ${status.progress?.total_requirements || 0} requirements processed`, className: 'success' },
    failed: { icon: '\u274C', message: `Job failed${status.error ? `: ${status.error}` : ''}`, className: 'failed' },
    cancelled: { icon: '\u26D4', message: 'Job was cancelled', className: 'cancelled' },
  };

  return (
    <AnimatePresence>
      {visible && (isProcessing || isTerminal) && (
        <motion.div
          key="progress-bar"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`top-progress-bar ${isTerminal ? `top-progress-bar--${statusConfig[status.status].className}` : ''}`}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <AnimatePresence mode="wait">
            {isTerminal ? (
              <motion.div
                key="terminal-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="top-progress-bar__status">
                  <span className={`top-progress-bar__status-message top-progress-bar__status-message--${statusConfig[status.status].className}`}>
                    <span>{statusConfig[status.status].icon}</span>
                    {statusConfig[status.status].message}
                  </span>
                  <button className="top-progress-bar__dismiss" onClick={handleDismiss}>
                    Dismiss
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="processing-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="top-progress-bar__content">
                  <div className="top-progress-bar__header">
                    <span className="top-progress-bar__label">
                      {status.status_message || 'Processing Requirements'}
                      <span className="top-progress-bar__job-id">
                        ({job?.id?.slice(0, 8)}...)
                      </span>
                    </span>
                    <div className="top-progress-bar__actions">
                      <div className="top-progress-bar__timing">
                        <span className="top-progress-bar__elapsed">
                          {formatTime(elapsedTime)}
                        </span>
                        {status.timing?.eta_seconds > 0 && (
                          <span className="top-progress-bar__eta">
                            ETA: {formatTime(status.timing.eta_seconds)}
                          </span>
                        )}
                      </div>
                      <button className="top-progress-bar__cancel" onClick={handleCancel}>
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="top-progress-bar__bar-row">
                    <div className="top-progress-bar__bar-container">
                      {status.progress?.total > 0 ? (
                        <div
                          className="top-progress-bar__bar-fill"
                          style={{ width: `${percent}%` }}
                        >
                          <span className="top-progress-bar__percent">{Math.round(percent)}%</span>
                        </div>
                      ) : (
                        <div className="top-progress-bar__bar-fill top-progress-bar__bar-fill--indeterminate" />
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TopProgressBar;
